import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { Buffer } from "buffer";
import { SkiUserEvent } from "./SkiUserEvent";
const returnUrlKey = "ski-oidc-return-url";
export default class SkiUserManager {
    constructor({ storage, automatic_silent_renew, accessTokenExpiringNotificationTimeInSeconds, onUserChange, claimsMapper, signoutOnTokenExpiration = true, ...options }) {
        Object.defineProperty(this, "userManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_user", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "claimsMapper", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_userChangeListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_eventType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "setUserChangeListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (listener) => {
                this._userChangeListener = listener;
                // make sure that any listener is notified of the latest changes
                listener(this.profile, this.accessToken);
            }
        });
        Object.defineProperty(this, "handleUserChanged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                if (this._user === e.user)
                    return;
                this._user = e.user;
                if (this._userChangeListener) {
                    this._userChangeListener(this.profile, this.accessToken);
                }
            }
        });
        Object.defineProperty(this, "initialize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                let user = await this.userManager.getUser();
                if (user && user.expired) {
                    user = null;
                }
                // force an immediate update to make sure that the user is set before the method exits
                this.handleUserChanged(new SkiUserEvent(this.eventType, user));
                if (user)
                    this.setUser(user);
                else
                    this.clearUser();
                return this.profile;
            }
        });
        Object.defineProperty(this, "setUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (user) => {
                // this will make sure that other instances of the SkiUserManager will get notified about user changes
                window.dispatchEvent(new SkiUserEvent(this.eventType, user));
            }
        });
        Object.defineProperty(this, "clearUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                // this will make sure that other instances of the SkiUserManager will get notified about user changes
                window.dispatchEvent(new SkiUserEvent(this.eventType, null));
            }
        });
        /**
         * Starts the login flow by redirecting to the login page.
         * The login is not complete until the @method completeSignin has been called by the Oidc callback page
         */
        Object.defineProperty(this, "signIn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (returnUrl) => {
                await this.userManager.signinRedirect({
                    state: {
                        returnUrl,
                    },
                });
            }
        });
        /**
         * Completes the signin flow by setting the user to the value given by the oidc client.
         */
        Object.defineProperty(this, "completeSignin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const user = await this.userManager.signinCallback();
                const returnUrl = user && user.state && user.state.returnUrl;
                return returnUrl;
            }
        });
        /**
         * Required as the oidc client event passes an event object, which is not compatible with the signOut method.
         */
        Object.defineProperty(this, "_internalSignout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                return await this.signOut();
            }
        });
        /**
         * Redirects to the signout page
         * @returns a Promise
         */
        Object.defineProperty(this, "signOut", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (returnUrl) => {
                if (returnUrl) {
                    localStorage.setItem(returnUrlKey, returnUrl);
                }
                const user = await this.userManager.getUser();
                if (user) {
                    return await this.userManager.signoutRedirect({
                        id_token_hint: user.id_token,
                        state: {
                            returnUrl,
                        },
                    });
                }
            }
        });
        /**
         *
         * @returns the url to redirect back to
         */
        Object.defineProperty(this, "completeSignout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                await this.userManager.signoutCallback();
                this.userManager.clearStaleState();
                this.clearUser();
                const returnUrl = localStorage.getItem(returnUrlKey);
                localStorage.removeItem(returnUrlKey);
                return returnUrl;
            }
        });
        /**
         * @returns whether the user is logged in or not
         */
        Object.defineProperty(this, "isLoggedIn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this._user !== null && !this._user.expired;
            }
        });
        /**
         * This is a convenience method to figure out if you should allow access to a page or either do a no-access or login redirect.
         * Using this method, requires you to set the meta.protected property on the route to mark which routes are behind login.
         * @param routeMetadata of the route which is being redirected to
         * @param defaultValues default values for the route meta properties. This is useful if you need the same value for most routes, and only want to override a few.
         * @returns whether you should
         */
        Object.defineProperty(this, "getPageRedirectAction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (routeMetadata, defaultValues) => {
                const allowedRoles = routeMetadata.allowedRoles ||
                    (defaultValues && defaultValues.allowedRoles
                        ? defaultValues.allowedRoles
                        : []);
                const isProtected = routeMetadata.protected !== undefined
                    ? routeMetadata.protected
                    : (defaultValues && defaultValues.protected) || false;
                if (isProtected && !this.isLoggedIn()) {
                    return { action: "login" };
                }
                if (isProtected &&
                    allowedRoles.length > 0 &&
                    this.profile &&
                    this.profile.roles &&
                    !this.profile.roles.some((role) => allowedRoles.includes(role))) {
                    return {
                        action: "no-access",
                        missingRoles: allowedRoles,
                    };
                }
                return {
                    action: "none",
                };
            }
        });
        this.userManager = new UserManager({
            ...options,
            // Dont change to use '??' it brakes in vue2 packages.
            accessTokenExpiringNotificationTimeInSeconds: accessTokenExpiringNotificationTimeInSeconds === undefined ? 60 : accessTokenExpiringNotificationTimeInSeconds,
            automaticSilentRenew: automatic_silent_renew === undefined ? true : automatic_silent_renew,
            userStore: new WebStorageStateStore({
                store: storage,
            }),
            response_type: "code",
            response_mode: "query",
            scope: "openid",
        });
        this.setupSigninSilent();
        this._userChangeListener = onUserChange;
        this.claimsMapper = claimsMapper;
        this.userManager.events.addUserLoaded(this.setUser);
        this.userManager.events.addUserUnloaded(this.clearUser);
        // Set up token expiration handling if enabled
        // This allows for disabling token expiration events in specific scenarios (e.g., microfrontends)
        if (signoutOnTokenExpiration) {
            this.userManager.events.addAccessTokenExpired(this._internalSignout);
        }
        this._eventType = options.authority + options.client_id;
        window.addEventListener(this._eventType, (e) => {
            const skiEvent = e;
            this.handleUserChanged(skiEvent);
        });
    }
    /**
     * Sets up the signinSilent method by modifying it to send with only 'openid' scope instead of 'openid offline_access'.
     * This is necessary because our IDP doesn't support the offline_access scope during refresh token requests.
     * For some reason the library sets this as required when it looks like its optional
     * https://openid.net/specs/openid-connect-core-1_0.html#OfflineAccess
     */
    setupSigninSilent() {
        const originalSigninSilent = this.userManager.signinSilent.bind(this.userManager);
        this.userManager.signinSilent = async (args) => {
            return originalSigninSilent({
                ...args,
                extraTokenParams: {
                    // Don't use optional chaining (?.) here due to Vue 2 compatibility issues
                    ...(args && args.extraTokenParams ? args.extraTokenParams : {}),
                    scope: 'openid',
                },
            });
        };
    }
    /**
     * The event type is used to identify the event that is being dispatched when the user changes.
     */
    get eventType() {
        if (this._eventType)
            return this._eventType;
        throw new Error("Event id not set");
    }
    get accessToken() {
        return this._user && this._user.access_token;
    }
    /**
     * The users profile is a parsed version of the claims in the access token.
     */
    get profile() {
        const user = this._user;
        if (!user)
            return null;
        const [, payload] = user.access_token.split(".") || [];
        const claims = JSON.parse(Buffer.from(payload, "base64").toString("utf-8"));
        const roleClaim = claims["https://ski.dk/identity/claims/roles"];
        // if the user has only one role, the value is a string, otherwise it is an array
        const roles = typeof roleClaim === "string" ? [roleClaim] : roleClaim || [];
        const profile = {
            email: claims["https://ski.dk/identity/claims/userid"],
            name: claims["https://ski.dk/identity/claims/fullname"],
            organizationId: claims["https://ski.dk/identity/claims/orgid"],
            applicationName: claims["https://ski.dk/identity/claims/applicationname"],
            orgType: claims["https://ski.dk/identity/claims/orgtype"],
            roles: roles,
        };
        if (this.claimsMapper) {
            Object.assign(profile, this.claimsMapper(claims));
        }
        return profile;
    }
}
